<script>
import FormInputBuilder from "../../../theme/molecules/FormInputBuilder.vue";
import BasicForm from "../../../theme/molecules/BasicForm.vue";
import Modal from "../../../theme/organisms/Modal.vue";
import {flattenObjectKeys, unFlattenObjectKeys} from "../../../services/Defaults";
import DropDownButton from "../../../theme/atoms/DropDownButton.vue";
import {getClasses} from "../../../services/ObjectsLoaderService";
import {SUBMISSION_STATUS} from "../services/SpaceFormConstants";
import RoleService from "../../../services/RoleService";
import FamilySelect from "../../../theme/atoms/FamilySelect.vue";
import PrintFormTemplate from "./PrintFormTemplate.vue";

export default {
    name: "ViewSubmissionModal",
    props: {
        value: {
            type: Object,
            required: false
        },
    },
    components: {
        PrintFormTemplate,
        DropDownButton,
        BasicForm,
        FormInputBuilder,
        Modal,
        FamilySelect
    },
    watch: {
        model: {
            handler() {
                if(this.model.data.parents) {
                    const parentsIds = JSON.parse(this.model.data.parents);
                    if(parentsIds.length > 0) {
                        this.autoLoadParents = false;
                        this.loadExistingParents(parentsIds);
                    } else {
                        this.showParentSelector = false;
                    }
                }
                this.$emit('input', this.model);
                this.formData = flattenObjectKeys(this.model.data)
            },
            deep: true
        },
        potentialParents: {
            handler() {
                this.potentialParents.forEach(v => {
                    if (!this.parents.map(v => v.value).includes(v.parent.id)) {
                        this.parents.push({value: v.parent.id, text: v.parent.name + ' ' + v.parent.surname})
                    }
                });
            },
            deep: true
        }
    },
    computed: {
        formFields() {
            return this.model.form?.definition ?? []
        },
        statusSubmittedOrRejected() {
            return ['submitted', 'rejected'].includes(this.model.status)
        }
    },
    data() {
        return {
            fields: [],
            model: {},
            errors: {},
            show: false,
            formData: {},
            classes: [],
            action: null,
            potentialParents: [],
            parents: [],
            SUBMISSION_STATUS,
            showParentSelector: false,
            printTemplate: false,
            autoLoadParents: true,
        }
    },
    methods: {
        open(model) {
            this.model = {...model};
            this.show = true;
            this.potentialParents = [];
            this.parents = [];
            this.mapParentDataToExistingParent({...model});
        },
        printForm() {
            this.printTemplate = true;

            setTimeout(() => {
                this.$refs.printTemplate.printForm();
            }, 500)
        },
        changeStatus(status) {
            this.action = status;
            axios.post(`/space/forms/submissions/${this.model.id}/edit`, {status}).then(response => {
                this.model.data = response.data;
                this.action = null;
                this.show = false;
            })
        },
        approve(group) {
            this.action = 'approved';
            if (this.formData.parents && typeof this.formData.parents !== 'string') {
                this.formData.parents = JSON.stringify(this.formData.parents);
            }

            axios.post(`/space/forms/submissions/${this.model.id}/edit`, {
                data: unFlattenObjectKeys(this.formData),
                status: 'approved',
                space_form_id: this.model.form.id,
                group_id: group.id
            }).then(response => {
                this.action = null;
                this.model = response.data.data;
                this.show = false;
                this.$notify({type: 'success', text: trans('general.page.space_forms.status.approved')});
            })
        },
        mapParentDataToExistingParent() {
            const parents = this.model.data?.parent ?? [];
            this.showParentSelector = parents.length > 0;

            parents.forEach((parent, index) => {
                axios.get(`/users/matching`, {
                    params: {
                        search: `email:${parent.email}`,
                        role: RoleService.PARENT
                    }
                }).then(response => {
                    if (response.data.data && this.statusSubmittedOrRejected) {
                        const existingParent = response.data.data;
                        if (parent.email !== existingParent?.email) {
                            return
                        }
                        this.model.data.parent.splice(index - this.potentialParents.length, 1);
                        existingParent.submittedParent = parent;
                        const potentialParent = {index: index, parent: existingParent}

                        this.potentialParents.push(potentialParent);
                    }
                })
            });
        },
        revertParents(parents) {
            if (parents) {
                const removedPotentials = this.potentialParents.filter(potentialParent => !parents.map(v => v.value).includes(potentialParent.parent.id));
                removedPotentials.forEach(potentialParent => {
                    this.model.data.parent.splice(potentialParent.index, 0, potentialParent.parent.submittedParent);
                });
                this.potentialParents = this.potentialParents.filter(potentialParent => parents.map(v => v.value).includes(potentialParent.parent.id));
            }
        },
        loadExistingParents(ids) {
            axios.get(`/parents/list-by-filters`, {params: {ids: ids}})
                .then(response => {
                const parents = response.data.data.data;

                if(parents.length > 0) {
                    this.showParentSelector = true;
                    parents.forEach(parent => {
                        this.parents.push({value: parent.id, text: parent.name + ' ' + parent.surname})
                    });
                }
            })
        }
    },
    mounted() {
        getClasses().then(classes => this.classes = classes);
    }
}
</script>

<template>
    <modal v-model="show" :title="model.form?.name" :id="this.$options.name" size="xl" @close="$emit('closed')">
        <basic-form>
            <div class="bg-gray-100 card card-bg-secondary p-3"
                 v-if="showParentSelector">
                <h5>{{ trans('general.page.space_forms.messages.parents_info_title') }}: </h5>
                <small
                    v-if="potentialParents.length">{{
                        trans('general.page.space_forms.messages.parents_info_note')
                    }}</small>
                <family-select
                    :showUsername="true"
                    :parents="parents"
                    v-model="formData.parents"
                    :auto-load="autoLoadParents"
                    @change="revertParents"/>
            </div>
            <form-input-builder v-if="model.form?.definition.length" :fields="formFields"
                                v-model="formData"></form-input-builder>
            <template #actions>
                <basic-button :text="trans('general.defaults.print')"
                              cls="btn-outline me-2 btn-md" icon="fi fi-rr-print" icon-position="right"
                              @click="printForm"></basic-button>

                <basic-button :text="trans('general.defaults.reject')"
                              :loading="action === SUBMISSION_STATUS.REJECTED"
                              cls="btn-danger me-2 btn-md" icon="fi fi-rr-cross" icon-position="right"
                              v-if="!['approved', 'rejected'].includes(model.status)"
                              @click="changeStatus(SUBMISSION_STATUS.REJECTED)"></basic-button>
                <drop-down-button id="approve"
                                  :loading="action === SUBMISSION_STATUS.APPROVED"
                                  @click="approve"
                                  icon="fi fi-rr-check" cls="btn-success me-2 "
                                  icon-position="right"
                                  v-if="['submitted', 'rejected'].includes(model.status)"
                                  :options="classes" :text="trans('general.defaults.approve')"
                                  :header="trans('general.page.space_forms.enroll_to')"></drop-down-button>
            </template>
            <template #submitButton>
                <div></div>
            </template>
        </basic-form>

        <print-form-template v-if="printTemplate" :formData="formData" ref="printTemplate"></print-form-template>
    </modal>
</template>
